/// <reference path="./tz-lookup.d.ts" />
/**
 * ICAO → IANA timezone for desk Local clocks.
 *
 * Curated rows (AIRPORT_HOURS and the catalog list below) win when present.
 * Any other ICAO in the coordinate table uses that field's real civil zone
 * from lat/lon — not America/Halifax, and not the PC clock. An ICAO with no
 * coordinates returns null. Quiet-hours defaults stay in curfew.ts and are
 * not a clock.
 */
import tzLookup from "tz-lookup";
import { lookupAirportCoords } from "./airportCoords";
import { AIRPORT_HOURS } from "./curfew";

/**
 * Catalog and ops fields that are not already on AIRPORT_HOURS.
 * Zones are the civil IANA zone for that field (DST rules included).
 */
const CATALOG_ZONES: Record<string, string> = {
  KBED: "America/New_York",
  KMMU: "America/New_York",
  KFRG: "America/New_York",
  KORH: "America/New_York",
  KSWF: "America/New_York",
  KHEF: "America/New_York",
  KBDL: "America/New_York",
  KPVD: "America/New_York",
  KBTV: "America/New_York",
  KIAD: "America/New_York",
  KPHL: "America/New_York",
  KBWI: "America/New_York",
  KMHT: "America/New_York",
  KBGR: "America/New_York",
  KPBI: "America/New_York",
  KMIA: "America/New_York",
  KCLT: "America/New_York",
  KFLL: "America/New_York",
  KFXE: "America/New_York",
  KOPF: "America/New_York",
  KTPA: "America/New_York",
  KRSW: "America/New_York",
  KAPF: "America/New_York",
  KMCO: "America/New_York",
  KJAX: "America/New_York",
  KBCT: "America/New_York",
  KCHS: "America/New_York",
  KMYR: "America/New_York",
  KILM: "America/New_York",
  KDAY: "America/New_York",
  KCHA: "America/New_York",
  CYHU: "America/Toronto",
  CYKZ: "America/Toronto",
  CYTZ: "America/Toronto",
  CYHM: "America/Toronto",
  CYKF: "America/Toronto",
  CYOO: "America/Toronto",
  KCPT: "America/Chicago",
  KGJT: "America/Denver",
  KAPA: "America/Denver",
  KDEN: "America/Denver",
  KSLC: "America/Denver",
  KABQ: "America/Denver",
  KSDL: "America/Phoenix",
  KPHX: "America/Phoenix",
  KSFO: "America/Los_Angeles",
  KLAX: "America/Los_Angeles",
  KSAN: "America/Los_Angeles",
  KSEA: "America/Los_Angeles",
  KPDX: "America/Los_Angeles",
  KLAS: "America/Los_Angeles",
  CYVR: "America/Vancouver",
  CYYJ: "America/Vancouver",
  CYYC: "America/Edmonton",
  CYEG: "America/Edmonton",
  PANC: "America/Anchorage",
  TJSJ: "America/Puerto_Rico",
  MYNN: "America/Nassau",
  TXKF: "Atlantic/Bermuda",
  TBPB: "America/Barbados",
  TNCM: "America/Lower_Princes",
  MDPC: "America/Santo_Domingo",
  MKJS: "America/Jamaica",
  MWCR: "America/Cayman",
  TNCA: "America/Aruba",
  TIST: "America/St_Thomas",
  MMUN: "America/Cancun",
  MMCZ: "America/Cancun",
  MMPR: "America/Mazatlan",
  MMSD: "America/Mazatlan",
  MMTO: "America/Mexico_City",
  MMMX: "America/Mexico_City",
  MMMY: "America/Monterrey",
};

const ZONES = new Map<string, string>();

for (const row of AIRPORT_HOURS) {
  const icao = row.icao.trim().toUpperCase();
  const zone = row.timeZone.trim();
  if (icao && zone) ZONES.set(icao, zone);
}

for (const [icao, zone] of Object.entries(CATALOG_ZONES)) {
  if (!ZONES.has(icao)) ZONES.set(icao, zone);
}

const COORD_ZONES = new Map<string, string | null>();

function intlSupportsZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return true;
  } catch {
    return false;
  }
}

/** Civil IANA zone from the coordinate table. Null when the field is not on file. */
function zoneFromCoordinates(icao: string): string | null {
  const cached = COORD_ZONES.get(icao);
  if (cached !== undefined) return cached;
  const coords = lookupAirportCoords(icao);
  if (!coords) {
    COORD_ZONES.set(icao, null);
    return null;
  }
  let zone: string | null = null;
  try {
    const hit = tzLookup(coords.lat, coords.lon).trim();
    if (hit && !hit.startsWith("Etc/") && hit !== "UTC" && intlSupportsZone(hit)) zone = hit;
  } catch {
    zone = null;
  }
  COORD_ZONES.set(icao, zone);
  return zone;
}

/** IANA zone for this ICAO, or null when the desk has no timezone on file. */
export function timeZoneForIcao(icao?: string | null): string | null {
  const key = (icao ?? "").trim().toUpperCase();
  if (!key) return null;
  return ZONES.get(key) ?? zoneFromCoordinates(key);
}
